import React, { useEffect, useRef, useState } from 'react';
import { createAgentVisualizer } from './agentVisualizerCore';

/**
 * Agent visualizer configuration — the animated identity the agent shows in
 * meeting tiles, external-meeting bot cameras (Zoom/Meet), and voice widgets.
 * Mirrors the LiveKit Agents-UI playground: style picker (aura/wave/radial/
 * grid/bar), agent-state preview, color hue, and color shift (aura only).
 *
 * Controlled component: `value` is the visualizerConfig object; `onChange`
 * receives the updated object. Parent owns persistence (basic-info PUT).
 */

const STYLES = ['aura', 'wave', 'radial', 'grid', 'bar'];
const STATES = ['connecting', 'listening', 'speaking', 'thinking'];

const DEFAULTS = { enabled: true, style: 'aura', color: '#f97316', colorShift: 0.05 };

// Hue slider <-> hex: keep saturation/lightness fixed at a vivid, on-dark value.
function hexToHue(hex) {
  const m = /^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/.exec(hex || '');
  if (!m) return 25;
  const [r, g, b] = [1, 2, 3].map((i) => parseInt(m[i], 16) / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max === min) return 0;
  const d = max - min;
  let h;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return Math.round(((h * 60) + 360) % 360);
}
function hueToHex(h) {
  const s = 0.85;
  const l = 0.58;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x]
    : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  const to = (v) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

export default function AgentVisualizerSection({ value, onChange }) {
  const cfg = { ...DEFAULTS, ...(value || {}) };
  const [previewState, setPreviewState] = useState('listening');
  const holderRef = useRef(null);
  const vizRef = useRef(null);

  // (Re)create the preview when style/color/colorShift change.
  useEffect(() => {
    if (!holderRef.current || !cfg.enabled) return undefined;
    const viz = createAgentVisualizer(holderRef.current, {
      style: cfg.style,
      color: cfg.color,
      colorShift: cfg.colorShift,
    });
    vizRef.current = viz;
    if (viz) viz.setState(previewState);
    return () => {
      viz?.destroy();
      vizRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.enabled, cfg.style, cfg.color, cfg.colorShift]);

  useEffect(() => {
    vizRef.current?.setState(previewState);
    // Fake speech rhythm in the preview so "speaking" visibly moves.
    if (previewState !== 'speaking') return undefined;
    const t = setInterval(() => {
      vizRef.current?.setVolume(0.25 + Math.random() * 0.65);
    }, 120);
    return () => {
      clearInterval(t);
      vizRef.current?.setVolume(0);
    };
  }, [previewState]);

  const set = (patch) => onChange({ ...cfg, ...patch });

  return (
    <div className="mt-6 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
      <div className="flex items-center justify-between mb-1">
        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Visualizer identity</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            The animated identity shown for this agent in meeting tiles, external meetings (Zoom/Meet), and voice widgets — reacts to listening, thinking, and speaking.
          </p>
        </div>
        <label className="inline-flex items-center cursor-pointer flex-shrink-0 ml-4">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={cfg.enabled}
            onChange={(e) => set({ enabled: e.target.checked })}
          />
          <div className="relative w-11 h-6 bg-gray-300 peer-checked:bg-orange-500 rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5" />
        </label>
      </div>

      {!cfg.enabled ? (
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
          Disabled — the agent shows its profile photo or initials instead.
        </p>
      ) : (
        <div className="grid md:grid-cols-[220px_1fr] gap-5 mt-4">
          {/* Live preview */}
          <div className="rounded-lg bg-gray-950 border border-gray-800 flex items-center justify-center" style={{ minHeight: 200 }}>
            <div ref={holderRef} style={{ width: 170, height: 170 }} />
          </div>

          {/* Controls */}
          <div className="space-y-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">Style</p>
              <div className="flex flex-wrap gap-1.5">
                {STYLES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => set({ style: s })}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wide border transition-colors ${
                      cfg.style === s
                        ? 'bg-orange-500 border-orange-500 text-white'
                        : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">Agent state (preview)</p>
              <div className="flex flex-wrap gap-1.5">
                {STATES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setPreviewState(s)}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wide border transition-colors ${
                      previewState === s
                        ? 'bg-gray-800 dark:bg-gray-200 border-transparent text-white dark:text-gray-900'
                        : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Color hue</p>
                <span className="text-xs font-mono text-gray-500 dark:text-gray-400 inline-flex items-center gap-1.5">
                  {cfg.color}
                  <span className="inline-block w-3 h-3 rounded-full border border-gray-300 dark:border-gray-600" style={{ background: cfg.color }} />
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="359"
                value={hexToHue(cfg.color)}
                onChange={(e) => set({ color: hueToHex(Number(e.target.value)) })}
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{ background: 'linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)' }}
              />
            </div>

            {cfg.style === 'aura' && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Color shift</p>
                  <span className="text-xs font-mono text-gray-500 dark:text-gray-400">{Number(cfg.colorShift).toFixed(2)}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={cfg.colorShift}
                  onChange={(e) => set({ colorShift: Number(e.target.value) })}
                  className="w-full h-2 rounded-full appearance-none cursor-pointer bg-gray-300 dark:bg-gray-600"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
